// The scanner's tools: what the home list offers and what each one opens. Adding a
// tool (Picking, Transfers) is one entry here. Gates use navTree's vocabulary and
// menuConfig's resolvers, so a tool and a nav entry with the same gate always agree.

import React from 'react';
import { allowsInstance, userCanAccess } from '../navigation/menuConfig';
import { INSTANCE, type Instance } from '../instance';
import AssemblyScreen, { UntrackedBoardsSwitch } from './Assembly/AssemblyScreen';
import MarkerFlow from './Marker/MarkerFlow';
import PickListTool from './Pick/PickListTool';
import ShipFlow from './Ship/ShipFlow';
import { AssemblyIcon, MarkerIcon, PickIcon, ShipIcon } from './icons';
import { capabilitiesFor } from './capabilities';
import type { ScannerTool, ToolViewer } from './types';

export { KIOSK_OPERATOR_CAPS, capabilitiesFor } from './capabilities';

const BOTH = ['firefly', 'swarm'];

export const SCANNER_TOOLS: ScannerTool[] = [
  {
    id: 'assembly',
    label: 'Assembly',
    description: 'Build or repair a drone by scanning its parts.',
    Icon: AssemblyIcon,
    instances: BOTH,
    render: (ctx) => <AssemblyScreen ctx={ctx} />,
    barControls: (ctx) => <UntrackedBoardsSwitch ctx={ctx} />,
  },
  {
    id: 'marker',
    label: 'Marker',
    description: 'Scan drones, then mark them all at once.',
    Icon: MarkerIcon,
    instances: BOTH,
    // Every action's route requires admin or operations. The kiosk's fixed set includes operations.
    require: 'operations',
    render: (ctx) => (
      <MarkerFlow
        operatorId={ctx.operatorId}
        isSharedMode={ctx.isSharedMode}
        setLeaveGuard={ctx.setLeaveGuard}
        initialActionId={ctx.toolParams.actionId as string | undefined}
      />
    ),
  },
  {
    id: 'ship',
    label: 'Ship',
    description: 'Scan the drones going out on an order.',
    Icon: ShipIcon,
    instances: BOTH,
    // The shipments route takes sales or operations; the kiosk's set carries both.
    require: 'operations',
    render: (ctx) => (
      <ShipFlow
        operatorId={ctx.operatorId}
        isSharedMode={ctx.isSharedMode}
        setLeaveGuard={ctx.setLeaveGuard}
        onReturn={() => ctx.openTool('marker', { actionId: 'mark-returned' })}
      />
    ),
  },
  {
    id: 'pick',
    label: 'Pick list',
    description: 'Move boxes to Assembly for the runs in progress.',
    Icon: PickIcon,
    instances: BOTH,
    // The pick route takes manufacturing; the kiosk's set carries it.
    require: 'manufacturing',
    render: () => <PickListTool />,
  },
];

/** The tools this deployment has and this viewer may use, in registry order. */
export function toolsFor(
  viewer: ToolViewer,
  inst: Instance = INSTANCE,
  tools: ScannerTool[] = SCANNER_TOOLS,
): ScannerTool[] {
  const caps = capabilitiesFor(viewer);
  return tools.filter((t) => allowsInstance(t, inst) && userCanAccess(t, caps));
}
